import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./financeTransfers.css";
import "../finance/finance.css";
import { fmt } from "../finance/finance";

const MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

const PROCESS_LABEL = { not_sent: "Не отправлено", sent: "Отправлено" };
const APPROVAL_LABEL = { pending: "Не принято", accepted: "Принято", rejected: "Отказано" };
const STATE_LABEL = { unpaid: "Не оплачено", paid: "Оплачено" };

// Ключ строки: у ещё не созданной записи id=null, поэтому используем orderNumber+id как уникальный ключ в React
const rowKey = (t) => `${t.orderNumber}-${t.id ?? "new"}`;

const FinanceTransfers = ({ API_URL }) => {
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/getFinanceTransfers`, { year, month });
            if (data.status === "success") setTransfers(data.transfers);
        } catch (err) {
            console.error("getFinanceTransfers:", err);
        } finally {
            setLoading(false);
        }
    }, [API_URL, year, month]);

    useEffect(() => { load(); }, [load]);

    const shiftMonth = (dir) => {
        let m = month + dir;
        let y = year;
        if (m < 1) { m = 12; y -= 1; }
        if (m > 12) { m = 1; y += 1; }
        setMonth(m);
        setYear(y);
    };

    const updateLocal = (key, field, value) => {
        setTransfers((prev) => prev.map((t) => (rowKey(t) === key ? { ...t, [field]: value } : t)));
    };

    const saveRow = async (t) => {
        try {
            const { data } = await axios.post(`${API_URL}/updateFinanceTransfer`, {
                ftId: t.id,
                orderNumber: t.orderNumber,
                amountUzs: t.amountUzs,
                invoiceNumber: t.invoiceNumber,
                processStatus: t.processStatus,
                approvalStatus: t.approvalStatus,
                cbRate: t.cbRate,
                state: t.state,
            });
            if (data.status !== "success") {
                toast.error(data.message || "Не удалось сохранить изменения");
                return false;
            }
            // если строка только что создалась (id был null) — запоминаем настоящий id
            if (!t.id && data.ftId) {
                setTransfers((prev) => prev.map((x) => (x === t || (x.orderNumber === t.orderNumber && x.id == null) ? { ...x, id: data.ftId } : x)));
            }
            return true;
        } catch (err) {
            console.error("updateFinanceTransfer:", err);
            toast.error("Не удалось сохранить изменения");
            return false;
        }
    };

    const saveField = async (t, field, value) => {
        const key = rowKey(t);
        updateLocal(key, field, value);
        await saveRow({ ...t, [field]: value });
    };

    // Состояние = "Оплачено" разрешаем ставить только после Процесс=Отправлено и Приказ=Принято
    const handleStateChange = async (t, value) => {
        if (value === "paid" && (t.processStatus !== "sent" || t.approvalStatus !== "accepted")) {
            toast.error("Сначала отметьте «Отправлено» и «Принято», затем можно закрыть оплату");
            return;
        }
        const key = rowKey(t);
        updateLocal(key, "state", value);
        const ok = await saveRow({ ...t, state: value });
        if (ok) {
            if (value === "paid") {
                toast.success("Остаток по заказу закрыт приходом через «Перечисление»");
            } else {
                toast.success("Оплата отменена — приход удалён, остаток по заказу восстановлен");
            }
            await load();
        }
    };

    const q = search.trim().toLowerCase();
    const filtered = transfers.filter(
        (t) => t.company.toLowerCase().includes(q) || (t.inn || "").includes(q) || String(t.orderNumber).includes(q)
    );

    return (
        <>
            <div className="fn-topbar">
                <div className="fn-search">⌕ <input placeholder="Поиск по компании / ИНН" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
                <div className="fn-pagetitle">Счет-фактуры</div>
                <div style={{ display: "flex", gap: 10 }}>
                    <button className="fn-month-btn" onClick={() => shiftMonth(-1)}>‹</button>
                    <button className="fn-month-btn">{MONTHS[month - 1]} {year}</button>
                    <button className="fn-month-btn" onClick={() => shiftMonth(1)}>›</button>
                </div>
            </div>

            {loading ? (
                <div className="fn-empty">Загрузка…</div>
            ) : filtered.length === 0 ? (
                <div className="fn-empty">Заказов с перечислением на этот месяц нет</div>
            ) : (
                <div className="fn-table-scroll">
                    <table className="fn-transfers">
                        <thead>
                            <tr>
                                <th>№</th><th>ИНН</th><th>Компания</th><th>Наименование</th><th>Дата</th><th>№ группы</th>
                                <th>Услуги</th>
                                <th>Сумма в $ (остаток)</th><th>Курс ЦБ</th><th>Сумма в UZS</th><th>Номер СЧФ</th><th>Процесс</th><th>Приказ</th><th>Состояние</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((t) => {
                                const key = rowKey(t);
                                const superseded = t.isSuperseded;
                                return (
                                    <tr key={key} className={superseded ? "fn-transfer-superseded" : ""}>
                                        <td className="mono">
                                            {t.orderNumber}
                                            {superseded && <span className="fn-pill debt" style={{ marginLeft: 8 }}>Изменён</span>}
                                        </td>
                                        <td className="mono">{t.inn || "—"}</td>
                                        <td>{t.company}</td>
                                        <td>{t.title}</td>
                                        <td>{new Date(t.date).toLocaleDateString("ru-RU")}</td>
                                        <td className="mono">{t.groupNumber || "—"}</td>

                                        {/* Услуги по заказу — из поля services (JSON), одна колонка, построчно */}
                                        <td className="fn-services-cell">
                                            {t.services && t.services.length ? (
                                                t.services.map((s, i) => (
                                                    <div key={i} className="fn-services-row">
                                                        <span>{s.title}</span>
                                                        <span className="mono">${fmt(s.total)}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <span style={{ color: "var(--fn-text-soft)" }}>—</span>
                                            )}
                                        </td>

                                        {/* Сумма в $ — остаток долга на момент выставления счёта, не редактируется */}
                                        <td className="mono">${fmt(t.debtAmount)}</td>

                                        <td className="fn-editable mono">
                                            <input
                                                type="number" placeholder="0"
                                                defaultValue={t.cbRate ?? ""}
                                                disabled={superseded}
                                                onBlur={(e) => saveField(t, "cbRate", e.target.value === "" ? null : Number(e.target.value))}
                                            />
                                        </td>
                                        <td className="fn-editable mono">
                                            <input
                                                type="number" placeholder="0"
                                                defaultValue={t.amountUzs ?? ""}
                                                disabled={superseded}
                                                onBlur={(e) => saveField(t, "amountUzs", e.target.value === "" ? null : Number(e.target.value))}
                                            />
                                        </td>
                                        <td className="fn-editable mono">
                                            <input
                                                type="text" placeholder="СЧФ-0000"
                                                defaultValue={t.invoiceNumber}
                                                disabled={superseded}
                                                onBlur={(e) => saveField(t, "invoiceNumber", e.target.value)}
                                            />
                                        </td>
                                        <td className="fn-editable">
                                            <select value={t.processStatus} disabled={superseded} onChange={(e) => saveField(t, "processStatus", e.target.value)}>
                                                {Object.entries(PROCESS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                            </select>
                                        </td>
                                        <td className="fn-editable">
                                            <select
                                                value={t.approvalStatus}
                                                disabled={superseded || t.processStatus !== "sent"}
                                                onChange={(e) => saveField(t, "approvalStatus", e.target.value)}
                                                title={t.processStatus !== "sent" ? "Сначала отметьте «Отправлено»" : undefined}
                                            >
                                                {Object.entries(APPROVAL_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                            </select>
                                        </td>
                                        <td className="fn-editable">
                                            <select
                                                value={t.state}
                                                disabled={superseded || t.approvalStatus !== "accepted"}
                                                onChange={(e) => handleStateChange(t, e.target.value)}
                                                title={t.approvalStatus !== "accepted" ? "Сначала дождитесь «Принято»" : undefined}
                                            >
                                                {Object.entries(STATE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default FinanceTransfers;